import { useId, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { Grid as GridJs } from '../../ui';

/* Pieces every policy tab view shares: the legacy AG Grid wrapper (it stays, D6), its outlined
   action buttons, the "Include in Policy Forms" checkbox, and the bordered group a grid sits in. */

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: ui/components/Grid (AG Grid stays legacy)
export const Grid = GridJs as any;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: rows the grids and the API hold
export type Row = Record<string, any>;

export function ActionButton({
  children,
  onClick,
  loading = false,
  disabled = false,
  tone = 'default',
  size = 'default',
  className,
}: {
  children: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  tone?: 'default' | 'danger';
  size?: 'default' | 'sm';
  className?: string;
}) {
  return (
    <Button
      variant="outline"
      size={size}
      disabled={disabled || loading}
      onClick={onClick}
      className={cn(
        'rounded-lg font-semibold',
        size === 'default' ? 'm-4 h-10 min-w-[120px] px-4' : 'm-2 h-7 min-w-20 px-2',
        tone === 'danger'
          ? 'border-action-danger-border text-action-danger hover:text-action-danger'
          : 'border-action-border text-primary hover:text-primary',
        className
      )}
    >
      {loading ? <Spinner /> : null}
      {children}
    </Button>
  );
}

/** The empty outlined box the legacy tab views put between their action buttons. It shows as a
    small grey mark and holds the spacing those rows have on verify. */
export function ActionSpacer() {
  return <div aria-hidden className="m-4 flex-none rounded-lg border border-action-border" />;
}

export function IncludeInFormsCheckbox({
  checked,
  onChange,
  label = 'Include in Policy Forms',
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
}) {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(next) => onChange(next === true)} />
      <Label htmlFor={id}>{label}</Label>
    </div>
  );
}

export function ActionRow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-row flex-wrap items-center justify-start gap-2', className)}>{children}</div>;
}

export function GridGroup({ children, className, label }: { children: ReactNode; className?: string; label?: string }) {
  return (
    <section
      {...(label ? { role: 'group', 'aria-label': label } : {})}
      className={cn('m-2.5 rounded-lg border border-panel-border bg-panel-bg p-4', className)}
    >
      {children}
    </section>
  );
}
